/**
 * ChassisForm — ficha a pantalla completa de un chasis multinodo.
 *
 * Pestañas:
 *   1. Identificación — nombre, fabricante, modelo, número de serie, tipo, estado
 *   2. Ubicación      — ubicación, rack, altura (U), posición
 *   3. Nodos          — slots y servidores montados (lectura, con enlace a su ficha)
 *   4. ⚡ Fuentes      — PSUs compartidas del chasis
 *
 * ESTÁNDAR DEL CMDB: una ficha existente abre EN LECTURA y se edita con el botón
 * «Editar»; al guardar vuelve a lectura; la copia y el borrado usan los diálogos
 * comunes (`common/CloneDialog`, `common/Dialog`).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, CheckIcon, XMarkIcon, TrashIcon, MapPinIcon, CubeIcon,
  ServerStackIcon, WrenchScrewdriverIcon, PencilSquareIcon, DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import LocationTreeSelect from './common/LocationTreeSelect';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import SwitchPsuSection from './SwitchPsuSection';
import FormCardsShell, { Sf2PaneGuard } from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import ChassisFormSummary from './ChassisFormSummary';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import DetailCrumb from './DetailCrumb';

const STATUS_OPTS = [
  { value: 'active',      label: 'Activo'       },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'inactive',    label: 'Inactivo'      },
];

const CHASSIS_TYPES = [
  'SuperServer', 'Twin (2 nodes)', 'Twin² (4 nodes)', 'BigTwin', 'MicroCloud',
  'Blade', 'Modular', 'Otro',
];

const EMPTY = {
  name: '', manufacturer: '', model: '', serial_number: '',
  chassis_type: '', total_node_slots: 2, rack_id: '', location_id: '',
  size_u: 2, position_u: '', notes: '', status: 'active',
  psu_count: '', psu_model: '', psu_wattage: '', psu_redundant: false,
};

export default function ChassisForm({ chassis, companyId, initialMode = 'create', onClose, onSave, variant = 'classic', navItems, navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const navigate = useNavigate();
  const isExisting = !!chassis?.id;
  // Modo lectura por defecto en las fichas existentes; el alta y el botón
  // «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isExisting || initialMode === 'edit');
  const [showClone, setShowClone] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  const [form, setForm]       = useState(EMPTY);
  const [isDirty, setIsDirty] = useState(false);
  // Nombre confirmado: el título se edita en línea en la cabecera y solo cuenta
  // como cambio cuando se confirma con ✓ o Enter (FICHA_ESTANDAR_TARJETAS §3.2).
  const [committedName, setCommittedName] = useState(chassis?.name || '');
  const nameDirty = isCards && isExisting && (form.name || '') !== (committedName || '');
  const [activeTab, setActiveTab] = useState('identificacion');
  const [apiError, setApiError]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [savedOk, setSavedOk]    = useState(false);
  const [locations, setLocations] = useState([]);
  const [racks, setRacks]         = useState([]);
  const [nodes, setNodes]         = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [newModel, setNewModel] = useState('');

  // Cambios sin guardar al pasar de ficha o cerrar. `handleSave` se define más
  // abajo: la función se evalúa al pulsar, no al montar.
  const navGuard = useNavGuard({
    dirty: isDirty || nameDirty,
    saving,
    entity: 'este chasis',
    actionLabel: 'pasas a otro chasis o cierras la ficha',
    save: () => handleSave({ keepEditing: editing }),
  });

  // ── Populate from prop ───────────────────────────────────────────────────
  useEffect(() => {
    if (chassis) {
      setForm({
        name:             chassis.name             || '',
        manufacturer:     chassis.manufacturer     || '',
        model:            chassis.model            || '',
        serial_number:    chassis.serial_number    || '',
        chassis_type:     chassis.chassis_type     || '',
        total_node_slots: chassis.total_node_slots ?? 2,
        rack_id:          chassis.rack_id          || '',
        location_id:      chassis.location_id      || '',
        size_u:           chassis.size_u           ?? 2,
        position_u:       chassis.position_u       || '',
        notes:            chassis.notes            || '',
        status:           chassis.status           || 'active',
        psu_count:        chassis.psu_count        ?? '',
        psu_model:        chassis.psu_model        || '',
        psu_wattage:      chassis.psu_wattage      ?? '',
        psu_redundant:    !!chassis.psu_redundant,
      });
      setCommittedName(chassis.name || '');
    }
  }, [chassis]);

  // ── Load supporting data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    api.getRacks(null, companyId).then(r => setRacks(r.data || [])).catch(() => {});
  }, [companyId]);

  const loadCatalogs = useCallback(async () => {
    if (!companyId) {
      setManufacturerOptions([]);
      setModelOptions([]);
      return;
    }
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'chassis' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'chassis' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) {}
  }, [companyId]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const saveManufacturerToCatalog = async (nameOverride) => {
    const name = nameOverride || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'chassis' }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar fabricante');
    }
  };

  const saveModelToCatalog = async (modelNameOverride) => {
    const modelName = modelNameOverride || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'chassis', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar modelo');
    }
  };

  // ── Load nodes for existing chassis ──────────────────────────────────────
  useEffect(() => {
    if (!chassis?.id || !companyId) return;
    api.getChassisNodes(chassis.id, companyId)
      .then(r => setNodes(r.data || []))
      .catch(() => {});
  }, [chassis?.id, companyId]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const filteredRacks = useMemo(() => {
    if (!form.location_id) return racks;
    return racks.filter(r => String(r.location_id) === String(form.location_id));
  }, [racks, form.location_id]);

  const selectedRack = useMemo(() =>
    filteredRacks.find(r => String(r.id) === String(form.rack_id)) ||
    racks.find(r => String(r.id) === String(form.rack_id)),
    [filteredRacks, racks, form.rack_id]
  );

  const rackHeight = selectedRack?.height_u ? Number(selectedRack.height_u) : null;
  const sizeVal    = Number(form.size_u || 0);
  const posVal     = form.position_u ? Number(form.position_u) : null;
  const bottomU    = posVal && sizeVal ? posVal - sizeVal + 1 : null;
  const rangeLabel = posVal && sizeVal
    ? (sizeVal === 1 ? `U${posVal}` : `U${posVal} – U${bottomU}`)
    : null;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const setField = (k, v) => {
    // En modo lectura los campos van deshabilitados; esto es la red de
    // seguridad por si algo se escapa (no se ensucia la ficha sin querer).
    if (!editing) return;
    setForm(prev => ({ ...prev, [k]: v }));
    setIsDirty(true);
  };

  /* Título editable en línea: en lectura los campos están desactivados, así que
     el nombre se escribe directamente en el formulario. */
  const handleTitleChange = (v) => {
    setForm(prev => ({ ...prev, name: v }));
    setIsDirty(true);
  };

  const commitTitle = async () => {
    if (!nameDirty && !isDirty) return;
    await handleSave();
  };

  const restoreTitle = () => {
    setForm(prev => ({ ...prev, name: committedName }));
    if (!editing) setIsDirty(false);
  };

  const irAChasis = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setApiError(null);
      onNavigate?.(destino);
    });
  };

  /* Cambiar de fabricante deja el modelo anterior sin sentido (§12.C14). */
  const handleManufacturerChange = (value) => {
    const antes = form.manufacturer || '';
    setField('manufacturer', value);
    if (antes !== (value || '') && form.model) setField('model', '');
  };

  const handleLocationChange = async (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, location_id: value, rack_id: '', position_u: '' }));
    setIsDirty(true);
    try {
      const r = await api.getRacks(value || null, companyId);
      setRacks(r.data || []);
    } catch (_) {}
  };

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: 'chassis', companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) setForm(prev => ({ ...prev, name: s }));
      setIsDirty(true);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  const handleRackChange = (e) => {
    const value = e.target.value;
    const rack = racks.find(r => String(r.id) === value);
    setForm(prev => ({
      ...prev,
      rack_id: value,
      location_id: rack?.location_id ? String(rack.location_id) : prev.location_id,
      position_u: '',
    }));
    setIsDirty(true);
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) { setApiError('El nombre es obligatorio'); return false; }
    setSaving(true); setApiError(null);
    try {
      const payload = {
        ...form,
        rack_id:          form.rack_id          ? parseInt(form.rack_id)          : null,
        location_id:      form.location_id      ? parseInt(form.location_id)      : null,
        total_node_slots: parseInt(form.total_node_slots) || 2,
        size_u:           parseInt(form.size_u) || 2,
        position_u:       form.position_u ? parseInt(form.position_u) : null,
        psu_count:        form.psu_count   ? parseInt(form.psu_count)   : null,
        psu_wattage:      form.psu_wattage ? parseInt(form.psu_wattage) : null,
        psu_model:        form.psu_model   || null,
        psu_redundant:    !!form.psu_redundant,
      };
      let creado = null;
      if (isExisting) {
        await api.updateChassis(chassis.id, payload, companyId);
      } else {
        creado = registroGuardado(await api.createChassis(payload, companyId));
      }
      setIsDirty(false);
      setCommittedName(form.name || '');
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      if (isExisting && !keepEditing) {
        // Guardar desde el botón normal vuelve a LECTURA (estándar del CMDB).
        setEditing(false);
      }
      return true;
    } catch (e) {
      setApiError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteChassis(chassis.id, companyId);
      setShowDelete(false);
      onSave();
      onClose();
    } catch (e) {
      setApiError(e.message);
      setShowDelete(false);
    }
  };

  const handleDiscard = () => {
    // En una ficha existente, descartar vuelve a modo LECTURA (así siempre hay
    // salida de la edición aunque no se haya tocado nada). En un alta, cierra.
    if (isExisting) {
      setForm({
        name:             chassis.name             || '',
        manufacturer:     chassis.manufacturer     || '',
        model:            chassis.model            || '',
        serial_number:    chassis.serial_number    || '',
        chassis_type:     chassis.chassis_type     || '',
        total_node_slots: chassis.total_node_slots ?? 2,
        rack_id:          chassis.rack_id          || '',
        location_id:      chassis.location_id      || '',
        size_u:           chassis.size_u           ?? 2,
        position_u:       chassis.position_u       || '',
        notes:            chassis.notes            || '',
        status:           chassis.status           || 'active',
        psu_count:        chassis.psu_count        ?? '',
        psu_model:        chassis.psu_model        || '',
        psu_wattage:      chassis.psu_wattage      ?? '',
        psu_redundant:    !!chassis.psu_redundant,
      });
      setEditing(false);
    } else {
      onClose();
    }
    setIsDirty(false);
    setApiError(null);
  };

  /* Volver cierra la ficha (en lectura antes no hacía nada porque reutilizaba
     el descarte, que solo sale de la edición). */
  const handleBack = () => {
    navGuard.request(() => {
      setIsDirty(false);
      onClose();
    });
  };

  const openCloneModal = () => {
    setCloneName(`${form.name || 'Chasis'} (copia)`);
    setCloneError('');
    setShowClone(true);
  };

  const handleCloneConfirm = async () => {
    const name = cloneName.trim();
    if (!name || cloning) return;
    setCloning(true);
    setCloneError('');
    try {
      await api.cloneChassis(chassis.id, { name }, companyId);
      setShowClone(false);
      onSave();
      onClose();
    } catch (e) {
      setCloneError(e.message || 'No se pudo copiar el chasis');
    } finally {
      setCloning(false);
    }
  };

  const STATUS_STYLES = {
    active:      { label: 'Activo',        bg: '#dcfce7', color: '#15803d' },
    maintenance: { label: 'Mantenimiento', bg: '#fef9c3', color: '#854d0e' },
    inactive:    { label: 'Inactivo',      bg: '#fee2e2', color: '#991b1b' },
  };
  const currentStatus = STATUS_STYLES[form.status] || STATUS_STYLES.active;
  const nodeCount     = isExisting ? (chassis.node_count || 0) : 0;

  const TABS = [
    { key: 'identificacion', label: 'Identificación' },
    { key: 'ubicacion',      label: 'Ubicación' },
    { key: 'nodos',          label: `Nodos (${nodeCount}/${form.total_node_slots})` },
    { key: 'fuentes',        label: '⚡ Fuentes' },
  ];

  // En tarjetas, Identificación y Ubicación se editan en General.
  const tabsCh = isCards
    ? [
        { key: 'identificacion', label: 'General' },
        { key: 'nodos',          label: `Nodos (${nodeCount}/${form.total_node_slots})` },
        { key: 'fuentes',        label: 'Fuentes' },
      ]
    : TABS;
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  return (
    <div className="dv-overlay">
      {/* ── Top bar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={handleBack} type="button">
          <ArrowLeftIcon />
          Chasis
        </button>
        <DetailCrumb label={form.name} />

        {/* ── Navegación entre fichas: siempre a la izquierda del spacer ── */}
        <FormPager
          items={navItems || []}
          index={navIndex}
          label="Chasis"
          disabled={saving}
          onChange={irAChasis}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isExisting && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon />
                  Editar
                </button>
              )}
              {isExisting && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={openCloneModal} title="Copiar este chasis con su configuración">
                  <DocumentDuplicateIcon /> Copiar
                </button>
              )}
              {isExisting && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDelete(true)} disabled={saving} title="Eliminar el chasis">
                  <TrashIcon />
                </button>
              )}
            </>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
            ● {currentStatus.label}
          </span>

          {!editing && (
            <>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={openCloneModal}
                title="Copiar este chasis con su configuración"
              >
                <DocumentDuplicateIcon />
                Copiar
              </button>
              <button
                className="dv-btn dv-btn-secondary"
                type="button"
                onClick={() => setEditing(true)}
                title="Editar la ficha"
              >
                <PencilSquareIcon />
                Editar
              </button>
            </>
          )}

          {editing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          {editing && (
            <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? <span className="dv-spinner" /> : <CheckIcon />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}

          {isExisting && (
            <button
              className="dv-btn dv-btn-danger"
              type="button"
              onClick={() => setShowDelete(true)}
              disabled={saving}
              title="Eliminar el chasis"
            >
              <TrashIcon />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')} title="Ubicación">
          <span className="dv-smart-btn-icon">
            <MapPinIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.85rem' }}>
            {locations.find(l => String(l.id) === String(form.location_id))?.name || 'Sin ubicación'}
          </span>
          <span className="dv-smart-btn-label">Ubicación</span>
        </button>

        {form.rack_id && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')} title="Rack">
            <span className="dv-smart-btn-icon">
              <CubeIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.85rem' }}>
              {selectedRack?.name || 'Rack'}
              {rangeLabel && <span style={{ fontWeight: 400, fontSize: '0.72rem' }}> {rangeLabel}</span>}
            </span>
            <span className="dv-smart-btn-label">Rack</span>
          </button>
        )}

        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('nodos')} title="Nodos">
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: '#059669' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#059669' }}>
            {nodeCount}/{form.total_node_slots}
          </span>
          <span className="dv-smart-btn-label">Nodos</span>
        </button>

        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('identificacion')} title="Hardware">
          <span className="dv-smart-btn-icon">
            <WrenchScrewdriverIcon style={{ width: 18, height: 18, color: '#6b7280' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: '#6b7280', fontSize: '0.8rem' }}>
            {[form.manufacturer, form.model].filter(Boolean).join(' · ') || '—'}
          </span>
          <span className="dv-smart-btn-label">Hardware</span>
        </button>

        {isExisting && (
          <DocSmartButton elementType="chassis" elementId={chassis.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<ServerStackIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={handleTitleChange}
                dirty={isDirty}
                titleDirty={nameDirty}
                onTitleCommit={commitTitle}
                onTitleReset={restoreTitle}
                placeholder="Nombre del chasis…"
                editing={editing}
                saving={saving}
                onSave={handleSave}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[form.manufacturer, form.model, form.location_name, form.rack_name].filter(Boolean).join(' › ')}
                badges={[
                  { text: currentStatus.label, color: form.status === 'active' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  { text: `${nodeCount}/${form.total_node_slots} nodos` },
                ]}
                chips={[
                  { value: form.size_u, label: 'U', onClick: () => setActiveTab('identificacion') },
                  ...(form.rack_name ? [{ value: form.rack_name, label: 'rack', onClick: () => setActiveTab('identificacion') }] : []),
                  { value: nodeCount, label: 'nodos', onClick: () => setActiveTab('nodos') },
                ]}
                extra={isExisting ? (
                  <DocSmartButton elementType="chassis" elementId={chassis.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="dv-record-title"
                    style={{ flex: 1 }}
                    placeholder="Nombre del chasis…"
                    value={form.name}
                    onChange={e => setField('name', e.target.value)} disabled={!editing}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="Sugerir nombre automático"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    {suggesting ? '…' : '✨'}
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsCh.map(tab => (
                  <button
                    key={tab.key}
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          >
          <Sf2PaneGuard isCards={isCards} editing={editing}>

          {/* ── Tab: General en tarjetas (dos columnas) ── */}
          {isCards && activeTab === 'identificacion' && (
            <div className={paneClass}>
              <ChassisFormSummary
                form={form}
                editing={editing}
                onFieldChange={setField}
                onManufacturerChange={handleManufacturerChange}
                manufacturerOptions={manufacturerOptions}
                modelOptions={filteredModelOptions}
                onCreateManufacturer={async (name) => {
                  handleManufacturerChange(name);
                  await saveManufacturerToCatalog(name);
                }}
                onCreateModel={async (name) => {
                  setField('model', name);
                  await saveModelToCatalog(name);
                }}
                chassisTypes={CHASSIS_TYPES}
                statusOptions={STATUS_OPTS}
                locations={locations}
                racks={filteredRacks}
                rackName={selectedRack?.name || form.rack_name || ''}
                onLocationChange={(id) => handleLocationChange({ target: { value: id } })}
                onRackChange={handleRackChange}
                rangeLabel={rangeLabel}
                rackHeight={rackHeight}
              />
            </div>
          )}

          {/* ── Tab: Identificación (ficha clásica) ── */}
          {!isCards && activeTab === 'identificacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Hardware</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Fabricante</label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={form.manufacturer}
                        onChange={e => { handleManufacturerChange(e.target.value); setAddingManufacturer(false); setNewManufacturer(''); }}
                      >
                        <option value="">— Seleccionar —</option>
                        {manufacturerOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setAddingManufacturer(v => !v)}
                        title="Añadir al maestro"
                        disabled={!editing}
                      >
                        Añadir
                      </button>
                    </div>
                    {addingManufacturer && (
                      <div className="input-group mt-2">
                        <input
                          className="form-control"
                          placeholder="Nuevo fabricante"
                          value={newManufacturer}
                          onChange={e => setNewManufacturer(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!newManufacturer.trim()) return;
                            const name = newManufacturer.trim();
                            setField('manufacturer', name);
                            await saveManufacturerToCatalog(name);
                            setNewManufacturer('');
                            setAddingManufacturer(false);
                          }}
                        >
                          Guardar
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }}>
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Modelo</label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={form.model}
                        onChange={e => { setField('model', e.target.value); setAddingModel(false); setNewModel(''); }}
                        disabled={!form.manufacturer && filteredModelOptions.length === 0}
                      >
                        <option value="">— Seleccionar —</option>
                        {filteredModelOptions.map(opt => (
                          <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setAddingModel(v => !v)}
                        disabled={!editing}
                        title="Añadir al maestro"
                      >
                        Añadir
                      </button>
                    </div>
                    {addingModel && (
                      <div className="input-group mt-2">
                        <input
                          className="form-control"
                          placeholder="Nuevo modelo"
                          value={newModel}
                          onChange={e => setNewModel(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!newModel.trim()) return;
                            const name = newModel.trim();
                            setField('model', name);
                            await saveModelToCatalog(name);
                            setNewModel('');
                            setAddingModel(false);
                          }}
                        >
                          Guardar
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingModel(false); setNewModel(''); }}>
                          Cancelar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo de chasis</label>
                    <select className="form-select" value={form.chassis_type}
                      onChange={e => setField('chassis_type', e.target.value)} disabled={!editing}>
                      <option value="">— Seleccionar —</option>
                      {CHASSIS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Inventario</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input className="form-control" value={form.serial_number}
                      onChange={e => setField('serial_number', e.target.value)} disabled={!editing} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.status}
                      onChange={e => setField('status', e.target.value)} disabled={!editing}>
                      {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Ubicación (ficha clásica; en tarjetas va en General) ── */}
          {!isCards && activeTab === 'ubicacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Ubicación <span className="text-danger">*</span></label>
                  <LocationTreeSelect
                    locations={locations}
                    value={form.location_id}
                    onChange={(id) => handleLocationChange({ target: { value: id } })}
                    placeholder="— Selecciona ubicación —"
                    disabled={!editing}
                  />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Rack</label>
                    <select className="form-select" value={form.rack_id}
                      onChange={handleRackChange}
                      disabled={!editing || filteredRacks.length === 0}>
                      <option value="">— Sin rack —</option>
                      {filteredRacks.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name || r.code}{r.code && r.name ? ` (${r.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Posición en rack</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Altura (U)</label>
                    <input className="form-control" type="number" min="1" step="1"
                      value={form.size_u}
                      onChange={e => setField('size_u', e.target.value)} disabled={!editing} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Posición U <span className="text-muted" style={{ fontSize: '0.75rem' }}>(opcional)</span>
                    </label>
                    <input className="form-control" type="number" min="1"
                      value={form.position_u || ''}
                      onChange={e => setField('position_u', e.target.value ? parseInt(e.target.value, 10) : '')}
                      placeholder="ej. 10" />
                    {rangeLabel && (
                      <div className="form-text">
                        Ocupará {rangeLabel}{rackHeight ? ` · Rack ${rackHeight}U` : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Nodos ── */}
          {activeTab === 'nodos' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Capacidad</div>
                <div className={isCards ? 'sf2-fields' : 'row g-3'}>
                  <div className={isCards ? 'sf2-field' : 'col-md-4'}>
                    <label className="form-label">Slots de nodo <small className="text-muted">(total físico)</small></label>
                    <input className="form-control" type="number" min="1" max="128"
                      value={form.total_node_slots}
                      onChange={e => setField('total_node_slots', e.target.value)} disabled={!editing} />
                  </div>
                  <div className={isCards ? 'sf2-field' : 'col-md-4'}>
                    <label className="form-label">Nodos instalados</label>
                    <div className="form-control-plaintext" style={{ fontWeight: 600 }}>
                      {nodeCount} / {form.total_node_slots}
                    </div>
                  </div>
                </div>

                {/* Node fill bar */}
                {(() => {
                  const total = parseInt(form.total_node_slots) || 1;
                  const pct   = Math.round((nodeCount / total) * 100);
                  const color = pct >= 90 ? '#dc3545' : pct >= 60 ? '#ffc107' : '#28a745';
                  return (
                    <div style={{ marginTop: 12, maxWidth: 400 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 3 }}>
                        <span>Ocupación</span>
                        <span style={{ fontWeight: 600, color }}>{pct}%</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: '#e9ecef', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .3s' }} />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Visual node grid */}
              <div className="dv-section">
                <div className="dv-section-title">Disposición de nodos</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {Array.from({ length: parseInt(form.total_node_slots) || 0 }, (_, i) => {
                    const nodeNum = i + 1;
                    const srv = nodes.find(n => n.chassis_node === nodeNum);
                    const isEmpty = !srv;
                    return (
                      <div
                        key={i}
                        title={srv ? `${srv.name} (${srv.status})` : `Nodo ${nodeNum} — vacío`}
                        style={{
                          width: 90, height: 56,
                          borderRadius: 6,
                          border: isEmpty ? '2px dashed #ccc' : '2px solid #28a745',
                          background: isEmpty ? 'var(--bg-secondary, #f8f9fa)' : '#dcfce7',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, color: isEmpty ? '#aaa' : '#15803d',
                          cursor: 'default',
                          transition: 'all .2s',
                        }}
                      >
                        <span style={{ fontWeight: 700, fontSize: 13 }}>N{nodeNum}</span>
                        {srv ? (
                          <span style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 80, textAlign: 'center' }}>
                            {srv.name}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10 }}>vacío</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Servers table */}
              {nodes.length > 0 && (
                <div className="dv-section">
                  <div className="dv-section-title">Servidores asignados</div>
                  <div className={isCards ? 'sf2-table-wrap' : undefined}>
                  <table className={isCards ? 'sf2-table' : 'table table-sm'} style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Nodo</th>
                        <th>Servidor</th>
                        <th>IP</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nodes.map(n => (
                        <tr key={n.id}>
                          <td><strong>N{n.chassis_node || '?'}</strong></td>
                          <td>
                            {/* Enlace cruzado: del chasis a la ficha del nodo. */}
                            <button
                              type="button"
                              className="list-btn-secondary"
                              style={{ padding: '0.15rem 0.5rem', fontSize: '0.8rem' }}
                              onClick={() => navigate(`/infrastructure/servers?destacar=${n.id}&abrir=1`)}
                              title="Abrir la ficha del servidor"
                            >
                              {n.name}
                            </button>
                          </td>
                          <td><code>{n.ip_address || '—'}</code></td>
                          <td>
                            <span className={`badge bg-${n.status === 'active' ? 'success' : n.status === 'maintenance' ? 'warning' : 'secondary'}`}>
                              {n.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Fuentes de alimentación ── */}
          {activeTab === 'fuentes' && (
            <div className={paneClass}>
              {!isExisting ? (
                <div className="dv-section">
                  <p className="dv-hint">Guarda el chasis primero para poder gestionar las fuentes de alimentación.</p>
                </div>
              ) : (
                <>
                  <div className="dv-section">
                    <SwitchPsuSection
                      elementId={chassis.id}
                      companyId={companyId}
                      fnGet={(id, cid) => api.getChassisComponents(id, cid)}
                      fnCreate={(id, data, cid) => api.createChassisComponent(id, data, cid)}
                      fnDelete={(id, compId, cid) => api.deleteChassisComponent(id, compId, cid)}
                    />
                  </div>
                  <div className="dv-section" style={{ marginTop: 24 }}>
                    <div className="dv-section-title">Conexiones PDU / Alimentación eléctrica</div>
                    <PowerConnectionsPanel
                      consumerType="chassis"
                      consumerId={chassis.id}
                      companyId={companyId}
                      rackId={form.rack_id ? Number(form.rack_id) : undefined}
                      elementName={form.name}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          </Sf2PaneGuard>

          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={commitTitle}
          />
          </FormCardsShell>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="chassis"
          ciId={chassis?.id}
          tipoLegible="el chasis"
        />
      </div>

      {/* ── Copiar chasis (diálogo común) ── */}
      {showClone && (
        <CloneDialog
          title="Copiar chasis"
          defaultName={cloneName}
          hint="Se copiarán el tipo, los slots de nodo, la altura, las PSUs, el fabricante, el modelo, las notas y la ubicación. No se copiarán el número de serie ni la posición en el rack, y el chasis nuevo nace sin nodos ni componentes compartidos."
          busy={cloning}
          error={cloneError}
          onClose={() => setShowClone(false)}
          onConfirm={handleCloneConfirm}
        />
      )}

      {/* ── Confirmar borrado (diálogo común) ── */}
      {showDelete && (
        <Dialog
          title="Eliminar chasis"
          busy={saving}
          onClose={() => setShowDelete(false)}
          footer={(
            <>
              <button className="list-btn-secondary" type="button" onClick={() => setShowDelete(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="list-btn-danger" type="button" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar el chasis <strong>{form.name}</strong>? Los servidores que
            lleva montados <strong>no se borran</strong>: quedan como servidores sueltos. Esta acción
            no se puede deshacer.
          </p>
        </Dialog>
      )}
    </div>
  );
}
